import React from "react";
import { useParams } from "react-router-dom";

import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarProvider,
  SidebarRail,
} from "ui/components/sidebar";
import { NavItem } from "@/components/nav/item";
import { DASHBOARD, EXTENSIONS, MESSAGE } from "icons";
import { Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "ui/components/badge";

import { BillableHeader } from "@/modules/work/billables/components/header";

import {
  BillableProvider,
  useBillable,
} from "@/modules/work/billables/context";
import { NavItemWithBadge } from "@/components/nav/nav-item-with-badge";
import { StatusDisplay } from "@/components/status/display";
import { TypeDisplay } from "@/components/type/display";
import { usePermissions } from "contexts/permission";

const BillableLayoutContent = (props) => {
  const { billableId } = useParams();
  const { billable, type } = useBillable();
  const permissions = usePermissions();

  const { data: activityCount = 0, isLoading } = useQuery({
    queryKey: ["billables", "detail", billableId, "activities", "count"],
    queryFn: async () => {
      const { fetchBillableActivityCount } =
        await import("@/modules/work/billables/activity/api");
      return fetchBillableActivityCount(billableId);
    },
    enabled: !!billableId,
  });

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  return (
    <div className="flex-1">
      <BillableHeader />
      <div className="flex flex-1">
        <SidebarProvider
          className="w-fit overflow-hidden max-h-dvh"
          name="project"
        >
          <Sidebar
            collapsible="icon"
            {...props}
            className="sidebar-height border-r bg-red-400 left-auto inset-y-auto top-28"
          >
            <SidebarHeader className="px-3 py-2 border-b">
              <div className="flex items-center gap-2">
                <StatusDisplay value={billable?.status} />
                <TypeDisplay value={type} />
              </div>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarMenu>
                  <NavItem
                    title="Details"
                    icon={DASHBOARD}
                    url={`/work/billables/${billableId}`}
                    matchExact={true}
                  />
                  <NavItem
                    title="Extensions"
                    icon={EXTENSIONS}
                    url={`/work/billables/${billableId}/extensions`}
                    requiredPermissions={{
                      type: "collection",
                      collection: "billable_extensions",
                      action: "read",
                    }}
                  />
                    <NavItemWithBadge
                      title="Activity"
                      icon={MESSAGE}
                      url={`/work/billables/${billableId}/activity`}
                      count={activityCount}
                      isCountLoading={isLoading}
                    />
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>
            <SidebarRail />
          </Sidebar>
        </SidebarProvider>

        <Outlet />
      </div>
    </div>
  );
};

const BillableLayout = (props) => {
  const { billableId } = useParams();
  return (
    <BillableProvider billableId={billableId}>
      <BillableLayoutContent {...props} />
    </BillableProvider>
  );
};

export default BillableLayout;
